// src/screens/IdVerificationScreen.js
// ============================================================================
// ID VERIFICATION SCREEN - STRIPE IDENTITY INTEGRATION
// 
// Uses Stripe Identity for automated KYC verification:
// - Secure document capture via Stripe's hosted flow
// - Automated ID verification and age extraction
// - Webhook-driven status updates
//
// Flow:
// 1. User taps "Start Verification"
// 2. Modal shows Stripe payment-processor disclosure
// 3. User acknowledges and proceeds
// 4. Opens Stripe Identity hosted verification page
// 5. User captures ID photo + selfie
// 6. Stripe processes and verifies
// 7. Webhook updates status → user returns to app
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Linking,
  RefreshControl,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useError } from '../components/ErrorContext';
import Footer from '../components/Footer';
import CompanyDisclosureModal, { CompanyNotice } from '../components/CompanyDisclosureModal';

// ============================================================================
// CONSTANTS
// ============================================================================

const COLORS = {
  BG: '#000',
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  GREEN: '#2ECC71',
  RED: '#E74C3C',
  ORANGE: '#F39C12',
  TEXT: '#EDEDED',
  DIM: '#888',
  BLUE: '#3498DB',
};

const STATUS_CONFIG = {
  unverified: {
    icon: '🪪',
    // Three-part explanation: what / when / why. Pre-2026-05-30 this was a
    // single bland sentence; users were abandoning at the "do I really
    // need to share my ID" hesitation point. Concrete why + timeline +
    // explicit answer to "can I play without it" cuts abandonment.
    title: 'Verify Your Identity',
    subtitle: "Quick photo of your ID + a selfie via Stripe Identity. Takes 2-3 minutes; approval usually within 10 minutes (sometimes manual review on edge cases).\n\nWhy we need this: identity and age (18+) verification is the only requirement to receive compensation. Nothing is credited before you verify. You can browse, enter events, and watch streams without it. On an unverified account it also removes the $500 daily limit on Super Crits, Rumbles and new memberships.",
    buttonText: 'Start Verification',
    buttonColor: COLORS.GOLD,
    canStart: true,
  },
  pending: {
    icon: '⏳',
    title: 'Verification In Progress',
    subtitle: 'Your verification is being processed. This usually takes just a few minutes.',
    buttonText: 'Continue Verification',
    buttonColor: COLORS.BLUE,
    canStart: true,
  },
  // User has SUBMITTED their documents and Stripe is reviewing — deliberately
  // no action button: a "Continue" here would re-enter the flow and mint (and
  // bill) a second verification session. The screen auto-polls until the
  // webhook resolves the result.
  processing: {
    icon: '⏳',
    title: 'Reviewing Your Documents',
    subtitle: 'Your documents were submitted and are being reviewed. This usually takes just a few minutes — this page updates automatically, no need to do anything.',
    buttonText: null,
    buttonColor: null,
    canStart: false,
  },
  requires_input: {
    icon: '⚠️',
    title: 'Additional Info Needed',
    subtitle: null, // Will use lastError from API
    buttonText: 'Continue Verification',
    buttonColor: COLORS.ORANGE,
    canStart: true,
  },
  needs_age_review: {
    icon: '⚠️',
    title: 'One More Step',
    subtitle: 'Your document was verified but we could not confirm your date of birth. Please run verification once more.',
    buttonText: 'Verify Again',
    buttonColor: COLORS.ORANGE,
    canStart: true,
  },
  rejected_underage: {
    icon: '🚫',
    title: 'Age Requirement Not Met',
    subtitle: 'You must be 18 or older to verify on CritCash. If you believe this is an error, contact support.',
    buttonText: 'Contact Support',
    buttonColor: COLORS.DIM,
    canStart: false,
  },
  verified: {
    icon: '✅',
    title: 'Identity Verified',
    subtitle: 'Your identity has been verified.',
    buttonText: null,
    buttonColor: null,
    canStart: false,
  },
  canceled: {
    icon: '❌',
    title: 'Verification Canceled',
    subtitle: 'Your verification was canceled. You can start a new verification at any time.',
    buttonText: 'Start New Verification',
    buttonColor: COLORS.GOLD,
    canStart: true,
  },
  rejected: {
    icon: '🚫',
    title: 'Verification Not Approved',
    subtitle: null, // Will use rejection reason from API
    buttonText: 'Contact Support',
    buttonColor: COLORS.DIM,
    canStart: false,
  },
  failed: {
    icon: '❌',
    title: 'Verification Failed',
    subtitle: 'There was an issue with your verification. Please try again.',
    buttonText: 'Try Again',
    buttonColor: COLORS.ORANGE,
    canStart: true,
  },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function IdVerificationScreen({ navigation, route }) {
  const { showError } = useError();
  const isFocused = useIsFocused();
  const functions = getFunctions();
  
  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState('unverified');
  const [verificationData, setVerificationData] = useState(null);
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  
  // Check for return from Stripe Identity
  const identityComplete = route?.params?.identity_complete;

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchStatus = useCallback(async () => {
    try {
      const getStatus = httpsCallable(functions, 'getIdentityVerificationStatus');
      const result = await getStatus();
      
      setStatus(result.data.status || 'unverified');
      setVerificationData(result.data);
      
    } catch (err) {
      console.error('Error fetching verification status:', err);
      // Don't show error on initial load - default to unverified
      if (!loading) {
        showError('Error', 'Unable to load verification status');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [functions, loading, showError]);

  // Initial load and refresh when focused
  useEffect(() => {
    if (isFocused) {
      fetchStatus();
    }
  }, [isFocused, fetchStatus]);

  // Handle return from Stripe Identity
  useEffect(() => {
    if (identityComplete) {
      // Clear the param to prevent re-triggering
      navigation.setParams({ identity_complete: undefined });
      
      // Show processing message and refresh status
      showError('Processing', 'Your verification is being processed. This may take a moment.');
      
      // Poll for status update (webhook may take a few seconds)
      const pollInterval = setInterval(() => {
        fetchStatus();
      }, 3000);
      
      // Stop polling after 30 seconds
      setTimeout(() => {
        clearInterval(pollInterval);
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [identityComplete, navigation, showError, fetchStatus]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchStatus();
  };

  // While a submission is in flight (pending/processing), poll so the screen
  // flips to the result on its own — the user has no button in these states.
  useEffect(() => {
    if (!isFocused || !['pending', 'processing'].includes(status)) return undefined;
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [isFocused, status, fetchStatus]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  // Called when user taps the main action button
  const handleStartVerificationPressed = () => {
    // Show disclosure modal first
    setShowDisclosureModal(true);
  };

  // Called when user accepts the disclosure and wants to proceed
  const handleDisclosureAccepted = async () => {
    setShowDisclosureModal(false);
    await proceedWithVerification();
  };

  // Called when user cancels the disclosure modal
  const handleDisclosureCanceled = () => {
    setShowDisclosureModal(false);
  };

  // Actually start the verification process
  const proceedWithVerification = async () => {
    setSubmitting(true);
    
    try {
      // Determine which function to call based on status. 'requires_input'
      // goes through CREATE (not retry): the Stripe session is still open in
      // that state, and createIdentityVerification now resumes it instead of
      // minting a fresh session (retry always cancels + restarts, losing the
      // user's progress). 'failed' has no live session, so retry is correct.
      // 'needs_age_review' also creates: its session already resolved at
      // Stripe, so a new one is required.
      const functionName = status === 'failed'
        ? 'retryIdentityVerification'
        : 'createIdentityVerification';
      
      const createSession = httpsCallable(functions, functionName);
      const result = await createSession();

      // Session already submitted and under review — nothing to reopen.
      if (result.data.processing) {
        setStatus('processing');
        showError(
          'Already Submitted',
          'Your documents are already being reviewed — no need to go through verification again. This page will update when the review finishes.'
        );
        return;
      }

      if (result.data.url) {
        // Open Stripe Identity verification flow
        const canOpen = await Linking.canOpenURL(result.data.url);
        
        if (canOpen) {
          await Linking.openURL(result.data.url);
          
          // Update local status to pending
          setStatus('pending');
          
          // Show instruction
          showError(
            'Complete Verification',
            'Please complete the verification in the opened window. Return here when done.'
          );
        } else {
          throw new Error('Unable to open verification page');
        }
      }
    } catch (err) {
      console.error('Error starting verification:', err);
      
      // Handle specific error cases
      if (err.code === 'already-exists') {
        showError('Already Verified', 'Your identity has already been verified.');
        fetchStatus();
      } else if (err.code === 'resource-exhausted') {
        showError('Too Many Attempts', err.message);
      } else {
        showError('Error', err.message || 'Unable to start verification. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactSupport = () => {
    const email = 'support@critcash.com';
    const subject = 'Identity Verification Issue';
    const body = 'Hi CritCash Support,\n\nI\'m having trouble with my identity verification.\n\nStatus: ' + status + '\n\nPlease help me resolve this issue.\n\nThank you!';
    
    Linking.openURL('mailto:' + email + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body));
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.unverified;
  
  // Override subtitle with API error if available
  const displaySubtitle = verificationData?.lastError || config.subtitle;

  // ============================================================================
  // RENDER - LOADING STATE
  // ============================================================================

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.GOLD} />
          <Text style={styles.loadingText}>Checking verification status...</Text>
        </View>
      </View>
    );
  }

  // ============================================================================
  // RENDER - MAIN CONTENT
  // ============================================================================

  return (
    <View style={styles.container}>
      {/* Company Disclosure Modal */}
      <CompanyDisclosureModal
        visible={showDisclosureModal}
        context="verification"
        onAccept={handleDisclosureAccepted}
        onCancel={handleDisclosureCanceled}
      />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={COLORS.GOLD}
          />
        }
      >
        {/* Status Icon */}
        <Text style={styles.statusIcon}>{config.icon}</Text>
        
        {/* Title */}
        <Text style={styles.title}>{config.title}</Text>
        
        {/* Subtitle / Error Message */}
        <Text style={[
          styles.subtitle,
          status === 'requires_input' && styles.errorText,
        ]}>
          {displaySubtitle}
        </Text>

        {/* Verification Badge (if verified) */}
        {status === 'verified' && (
          <View style={styles.verifiedBadge}>
            <Text style={styles.verifiedIcon}>✓</Text>
            <View style={styles.verifiedInfo}>
              <Text style={styles.verifiedLabel}>Verified</Text>
              {verificationData?.verifiedAt && (
                <Text style={styles.verifiedDate}>
                  {new Date(verificationData.verifiedAt).toLocaleDateString()}
                </Text>
              )}
            </View>
            {verificationData?.ageVerified && (
              <View style={styles.ageBadge}>
                <Text style={styles.ageBadgeText}>18+</Text>
              </View>
            )}
          </View>
        )}

        {/* Age Verification Status (if verified but underage) */}
        {status === 'verified' && verificationData?.ageVerified === false && (
          <View style={styles.warningBox}>
            <Text style={styles.warningIcon}>⚠️</Text>
            <Text style={styles.warningText}>
              Age verification incomplete. Contact support@critcash.com to finish it. Until then you can't receive compensation and the $500 daily purchase limit applies.
            </Text>
          </View>
        )}

        {/* Document Types Info */}
        {config.canStart && (
          <View style={styles.infoBox}>
            <Text style={styles.infoTitle}>Accepted Documents</Text>
            <View style={styles.documentList}>
              <Text style={styles.documentItem}>🪪 Driver's License</Text>
              <Text style={styles.documentItem}>🛂 Passport</Text>
              <Text style={styles.documentItem}>🆔 National ID Card</Text>
            </View>
            <Text style={styles.infoNote}>
              You'll need to take a photo of your ID and a quick selfie.
            </Text>
          </View>
        )}

        {/* Company Disclosure Notice (inline) */}
        {config.canStart && (
          <CompanyNotice style={styles.companyNotice} />
        )}

        {/* Privacy Note */}
        {config.canStart && (
          <View style={styles.privacyBox}>
            <Text style={styles.privacyIcon}>🔒</Text>
            <Text style={styles.privacyText}>
              Your information is securely processed by Stripe and encrypted at rest.
              We only store verification status, not your actual ID images.
            </Text>
          </View>
        )}

        {/* Action Button */}
        {config.buttonText && (
          <TouchableOpacity
            style={[
              styles.actionButton,
              { backgroundColor: config.buttonColor },
              submitting && styles.buttonDisabled,
            ]}
            onPress={
              status === 'rejected' || status === 'rejected_underage'
                ? handleContactSupport
                : handleStartVerificationPressed
            }
            disabled={submitting}
          >
            {submitting ? (
              <ActivityIndicator color="#000" />
            ) : (
              <Text style={[
                styles.actionButtonText,
                config.buttonColor === COLORS.DIM && styles.actionButtonTextLight,
              ]}>
                {config.buttonText}
              </Text>
            )}
          </TouchableOpacity>
        )}

        {/* Secondary Actions */}
        {status === 'verified' && (
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => navigation.navigate('Verification')}
          >
            <Text style={styles.secondaryButtonText}>
              View All Verifications
            </Text>
          </TouchableOpacity>
        )}

        {/* Back Button */}
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
        >
          <Text style={styles.backButtonText}>← Go Back</Text>
        </TouchableOpacity>

        {/* Support Link */}
        {config.canStart && (
          <TouchableOpacity
            style={styles.supportLink}
            onPress={handleContactSupport}
          >
            <Text style={styles.supportLinkText}>
              Having trouble? Contact Support
            </Text>
          </TouchableOpacity>
        )}
      </ScrollView>
      
      <Footer navigation={navigation} />
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.BG,
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 40,
    paddingBottom: 100,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: COLORS.DIM,
    fontSize: 16,
    marginTop: 16,
  },
  
  // Status Display
  statusIcon: {
    fontSize: 64,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: COLORS.GOLD,
    textAlign: 'center',
    marginBottom: 12,
  },
  subtitle: {
    fontSize: 16,
    color: COLORS.DIM,
    textAlign: 'center',
    marginBottom: 24,
    paddingHorizontal: 20,
    lineHeight: 24,
  },
  errorText: {
    color: COLORS.ORANGE,
  },
  
  // Verified Badge
  verifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: COLORS.GREEN,
  },
  verifiedIcon: {
    fontSize: 24,
    color: COLORS.GREEN,
    marginRight: 12,
  },
  verifiedInfo: {
    flex: 1,
  },
  verifiedLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: COLORS.GREEN,
  },
  verifiedDate: {
    fontSize: 14,
    color: COLORS.DIM,
    marginTop: 2,
  },
  ageBadge: {
    backgroundColor: COLORS.GREEN,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  ageBadgeText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#000',
  },
  
  // Warning Box
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#3D2C00',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    width: '100%',
    maxWidth: 400,
  },
  warningIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  warningText: {
    flex: 1,
    fontSize: 14,
    color: COLORS.ORANGE,
    lineHeight: 20,
  },
  
  // Info Box
  infoBox: {
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    width: '100%',
    maxWidth: 400,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: COLORS.TEXT,
    marginBottom: 12,
  },
  documentList: {
    marginBottom: 12,
  },
  documentItem: {
    fontSize: 15,
    color: COLORS.TEXT,
    marginBottom: 8,
    paddingLeft: 8,
  },
  infoNote: {
    fontSize: 13,
    color: COLORS.DIM,
    fontStyle: 'italic',
  },

  // Company Notice (inline, from CompanyDisclosureModal)
  companyNotice: {
    marginBottom: 16,
    width: '100%',
    maxWidth: 400,
  },
  
  // Privacy Box
  privacyBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(52, 152, 219, 0.1)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    width: '100%',
    maxWidth: 400,
  },
  privacyIcon: {
    fontSize: 20,
    marginRight: 12,
    marginTop: 2,
  },
  privacyText: {
    flex: 1,
    fontSize: 13,
    color: COLORS.DIM,
    lineHeight: 20,
  },
  
  // Buttons
  actionButton: {
    width: '100%',
    maxWidth: 400,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: 16,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  actionButtonText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000',
  },
  actionButtonTextLight: {
    color: COLORS.TEXT,
  },
  secondaryButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    marginBottom: 16,
  },
  secondaryButtonText: {
    fontSize: 16,
    color: COLORS.BLUE,
    textDecorationLine: 'underline',
  },
  backButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    marginTop: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: COLORS.TEXT,
    textDecorationLine: 'underline',
  },
  supportLink: {
    marginTop: 24,
    paddingVertical: 12,
  },
  supportLinkText: {
    fontSize: 14,
    color: COLORS.DIM,
    textDecorationLine: 'underline',
  },
});